'use client';

import type {
  Control,
  FieldValues,
  Path,
} from '@edgevanta/ui/lib/react-hook-form';
import Form from '@edgevanta/ui/components/form';
import Input from '@edgevanta/ui/components/input';
import { useFormReadOnly } from '@edgevanta/ui/hooks/use-form-read-only';

interface FormEmailProps<T extends FieldValues> {
  readonly control: Control<T>;
  readonly name: Path<T>;
  readonly label: string;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly className?: string;
  readonly description?: string;
  readonly autoComplete?: string;
}

/**
 * Form email input component that automatically converts input to lowercase
 * and trims whitespace as the user types, ensuring consistent email formatting
 * throughout the app.
 *
 * All email addresses in this application are stored and processed as lowercase
 * to ensure consistency and prevent duplicate accounts with different casing.
 * Whitespace is trimmed to handle accidental spaces during input.
 *
 * @example
 * <FormEmail
 *   control={form.control}
 *   name="email"
 *   label="Email Address"
 *   placeholder="user@example.com"
 * />
 */
export function FormEmail<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  disabled = false,
  readOnly,
  className,
  description,
  autoComplete = 'email',
}: FormEmailProps<T>) {
  const resolvedReadOnly = useFormReadOnly(readOnly);

  return (
    <Form.Field
      control={control}
      name={name}
      render={({ field }) => (
        <Form.Item className={className}>
          <Form.Label>{label}</Form.Label>
          <Form.Control>
            <Input
              {...field}
              autoCapitalize="none"
              autoComplete={autoComplete}
              autoCorrect="off"
              disabled={disabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const normalizedValue = e.target.value.toLowerCase().trim();
                field.onChange(normalizedValue);
              }}
              placeholder={placeholder}
              readOnly={resolvedReadOnly}
              spellCheck="false"
              type="email"
            />
          </Form.Control>
          {description && <Form.Description>{description}</Form.Description>}
          <Form.Message />
        </Form.Item>
      )}
    />
  );
}
